import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, parseISO } from "date-fns";
import {
  Button,
  Divider,
  Form,
  Icon,
  Loader,
  Message,
  Segment,
} from "semantic-ui-react";

import { datePlusDays, formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";

import api from "api";
import util from "utils/utils";

import { DateInput } from "components/lib/DateInputs";

type CalendarSettingsProps = {
  forType: "group" | "challenge";
  forId: string;
  calendarId: string;
};

type TargetUsers = "members" | "calendarAccess";

type CalendarMoveWholeSectionProps = {
  calendarId: string;
  calendarStartYmd: string;
  invalidateCalendar: () => void;
};

const CalendarMoveWholeSection = ({
  calendarId,
  calendarStartYmd,
  invalidateCalendar,
}: CalendarMoveWholeSectionProps) => {
  const [proposedStartYmd, setProposedStartYmd] = useState(calendarStartYmd);

  const shiftDiffDays = useMemo(
    () =>
      differenceInCalendarDays(
        parseISO(proposedStartYmd),
        parseISO(calendarStartYmd),
      ),
    [calendarStartYmd, proposedStartYmd],
  );

  const shiftCalendar = useCallback(() => {
    if (shiftDiffDays === 0) {
      return;
    }
    api.calendars.shiftCalendarEvents(
      calendarId,
      { shiftDays: Math.abs(shiftDiffDays), shiftForward: shiftDiffDays > 0 },
      () => {
        toast.success("Calendar moved successfully");
        invalidateCalendar();
      },
      (error) => {
        toast.error(error.message);
      },
    );
  }, [shiftDiffDays, calendarId, invalidateCalendar]);

  return (
    <>
      <h5 style={{ marginBottom: 5 }}>Move whole calendar</h5>
      <p>
        Pick the new start date for this calendar. All events, milestones, and
        the calendar end date move by the same number of days so the schedule
        stays the same, just on different dates.
      </p>
      <Form>
        <Form.Field>
          <label htmlFor="calendarShiftStartDate">
            New calendar start date
          </label>
          <DateInput
            clearable
            id="calendarShiftStartDate"
            placeholder="Select a new start date"
            value={proposedStartYmd}
            dateFormat="yyyy-MM-dd"
            onChange={(_e, { value }) =>
              setProposedStartYmd(String(value ?? ""))
            }
          />
        </Form.Field>
      </Form>
      {shiftDiffDays ?
        <Message style={{ marginTop: 12 }}>
          <Message.Content>
            {shiftDiffDays > 0 ?
              `Everything moves forward by ${util.pluralise(shiftDiffDays, "day", "days")} (start becomes ${formatDate(parseISO(proposedStartYmd), "dd/MM/yyyy")}).`
            : `Everything moves back by ${util.pluralise(Math.abs(shiftDiffDays), "day", "days")} (start becomes ${formatDate(parseISO(proposedStartYmd), "dd/MM/yyyy")}).`
            }
            <Divider />
            <Button onClick={shiftCalendar} content="Apply move" primary />
          </Message.Content>
        </Message>
      : null}
    </>
  );
};

const CalendarSettings = ({
  forType,
  forId,
  calendarId,
}: CalendarSettingsProps) => {
  const navigate = useNavigate();

  const calendarQuery = useQuery(
    initUseQuery(api.calendars.getCalendar, [calendarId], {
      isEnabled: !!calendarId,
    }),
  );

  const calendar = calendarQuery.data?.calendar;
  const targetUsers = (calendar?.targetUsers || "members") as TargetUsers;

  const from = useMemo(
    () => (calendar?.start ? new Date(calendar.start) : new Date()),
    [calendar],
  );
  const nextMonth = useMemo(() => new Date(from), [from]);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const to = useMemo(
    () => (calendar?.end ? new Date(calendar.end) : nextMonth),
    [calendar, nextMonth],
  );

  const toMinDate = datePlusDays(from, 1);
  const fromMaxDate = datePlusDays(to, -1);

  const calendarStartYmd = useMemo(
    () => (from ? new Date(from).toISOString().split("T")[0] : ""),
    [from],
  );

  const invalidateCalendar = useCallback(() => {
    api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
  }, [calendarId]);

  const setFrom = useCallback(
    (value: string) => {
      api.calendars.updateCalendar(
        calendarId,
        { start: value },
        () => {
          invalidateCalendar();
        },
        (error) => {
          toast.error(error.message);
        },
      );
    },
    [calendarId, invalidateCalendar],
  );

  const setTo = useCallback(
    (value: string) => {
      api.calendars.updateCalendar(
        calendarId,
        { end: value },
        () => {
          invalidateCalendar();
        },
        (error) => {
          toast.error(error.message);
        },
      );
    },
    [calendarId, invalidateCalendar],
  );

  const setTargetUsers = useCallback(
    (value: TargetUsers) => {
      api.calendars.update(
        calendarId,
        { targetUsers: value },
        () => {
          invalidateCalendar();
          api.invalidateQueries(api.calendars.get, [calendarId]);
          api.invalidateQueries(api.calendars.getAll, [forType, forId]);
        },
        (error) => {
          toast.error(error.message);
        },
      );
    },
    [calendarId, forId, forType, invalidateCalendar],
  );

  const getCalendarSyncUrl = useCallback(() => {
    api.calendars.getCalendarSyncUrl(
      calendarId,
      ({ syncUrl }) => {
        navigator.clipboard.writeText(syncUrl);
        toast.success("Calendar link copied to clipboard");
      },
      (error) => {
        toast.error(error.message);
      },
    );
  }, [calendarId]);

  const eventsOutsideRange = useMemo(() => {
    if (!calendar?.start || !calendar?.end) {
      return [];
    }
    const startBound = new Date(calendar.start);
    const endBound = new Date(calendar.end);
    return (calendar.events || [])
      .filter(
        (event) =>
          event.id !== "openPeriod" &&
          event.eventType !== "background" &&
          event.type !== "openPeriod",
      )
      .filter((event) => {
        if (!event.from) {
          return false;
        }
        const eventFrom = new Date(event.from);
        return eventFrom < startBound || eventFrom > endBound;
      });
  }, [calendar]);

  const deleteCalendar = useCallback(() => {
    util
      .confirm(
        "Delete calendar?",
        "Deleting this calendar removes it for everyone in the group, including all scheduled events. This action cannot be undone. Are you sure?",
      )
      .then(() => {
        let navigateTo = "";
        if (forType === "group") {
          navigateTo = `/groups/${forId}/settings/calendars`;
        }
        api.calendars.delete(
          calendarId,
          () => {
            api.invalidateQueries(api.calendars.getAll, [forType, forId]);
            api.invalidateQueries(api.calendars.get, [calendarId]);
            api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
            if (navigateTo) {
              navigate(navigateTo);
            }
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [calendarId, forType, forId, navigate]);

  if (calendarQuery.isLoading) {
    return (
      <Segment attached>
        <Loader active inline="centered" />
      </Segment>
    );
  }

  if (calendarQuery.isError) {
    return (
      <Segment attached>
        <Message negative>
          Could not load calendar settings. You may need calendar edit access.
        </Message>
        <Divider section />
        <Message warning>
          <Message.Header>Delete calendar</Message.Header>
          <Message.Content>
            Remove this calendar from the group. All scheduled events will be
            lost.
            <Divider />
            <Button
              basic
              onClick={() => deleteCalendar()}
              content="Delete"
              icon="trash"
            />
          </Message.Content>
        </Message>
      </Segment>
    );
  }

  return (
    <Segment attached style={{ marginTop: 0 }}>
      <h5>Calendar timeframe</h5>
      <Form>
        <Form.Group>
          <Form.Field>
            <label htmlFor="calendarStartDate">Start date:</label>
            <DateInput
              id="calendarStartDate"
              value={from.toISOString().split("T")[0] ?? ""}
              dateFormat="yyyy-MM-dd"
              onChange={(_e, { value }) => setFrom(String(value ?? ""))}
              maxDate={fromMaxDate.toISOString().split("T")[0] ?? ""}
            />
          </Form.Field>

          <Form.Field>
            <label htmlFor="calendarEndDate">End date:</label>
            <DateInput
              id="calendarEndDate"
              value={to.toISOString().split("T")[0] ?? ""}
              dateFormat="yyyy-MM-dd"
              onChange={(_e, { value }) => setTo(String(value ?? ""))}
              minDate={toMinDate.toISOString().split("T")[0] ?? ""}
            />
          </Form.Field>
        </Form.Group>
      </Form>
      {eventsOutsideRange.length > 0 ?
        <Message
          warning
          size="small"
          header="Configuration issue"
          content={
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              {eventsOutsideRange.map((event) => (
                <div key={event.id}>
                  The event <strong>{event.title}</strong> on the{" "}
                  {formatDate(event.from, "dd/MM/yyyy")} is currently outside of
                  the calendar date range.
                </div>
              ))}
            </div>
          }
          style={{ marginBottom: 20 }}
        />
      : null}
      <Divider hidden />
      {calendarStartYmd ?
        <CalendarMoveWholeSection
          key={`${calendarId}-${calendarStartYmd}`}
          calendarId={calendarId}
          calendarStartYmd={calendarStartYmd}
          invalidateCalendar={invalidateCalendar}
        />
      : null}
      {forType === "group" ?
        <>
          <Divider hidden />
          <h5>Event alerts</h5>
          <p>
            Choose who receives notifications configured on calendar events.
          </p>
          <Form>
            <Form.Group grouped>
              <Form.Radio
                label="Event members"
                checked={targetUsers === "members"}
                onChange={() => setTargetUsers("members")}
              />
              <Form.Radio
                label="All users with access to this calendar"
                checked={targetUsers === "calendarAccess"}
                onChange={() => setTargetUsers("calendarAccess")}
              />
            </Form.Group>
          </Form>
        </>
      : null}
      <Divider hidden />
      <h5>Sync with your calendar</h5>
      <p>
        Subscribe in Google Calendar, Apple Calendar, Outlook, or other apps.
        Updates to this calendar will appear in your app.
      </p>
      <Button size="small" onClick={() => getCalendarSyncUrl()}>
        <Icon name="calendar" />
        Get calendar link
      </Button>

      {forType === "group" ?
        <>
          <Divider section />
          <Message warning>
            <Message.Header>Delete calendar</Message.Header>
            <Message.Content>
              Deleting this calendar removes it for everyone, including all
              scheduled events. This action cannot be undone.
              <Divider />
              <Button
                basic
                onClick={() => deleteCalendar()}
                content="Delete"
                icon="trash"
              />
            </Message.Content>
          </Message>
        </>
      : null}
    </Segment>
  );
};

export default CalendarSettings;
